import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FileText, Plus, Search } from 'lucide-react';
import { getUrl, remove, uploadData } from 'aws-amplify/storage';
import {
  useAddAdmissionsDocumentMutation,
  useGetAdmissionsApplicationQuery,
} from '../../../../state/domainRouterApi';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import { useAdmissionsDocuments } from '../../adapters/liveData';
import { resolveCognitoProfile } from '../../../../utils/cognito-profile';
import { AppDialogFrame } from '../../../../components/overlays/AppDialogFrame';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const readString = (value: unknown): string => String(value ?? '').trim();
const readStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((entry) => readString(entry)).filter(Boolean) : [];

type StudentOption = {
  key: string;
  label: string;
  childApplicationId?: string;
};

const asUnixSeconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDate = (epochSeconds: unknown): string => {
  const value = asUnixSeconds(epochSeconds);
  if (value == null || value <= 0) return '';
  try {
    return new Date(value * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const toErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  const text = String(error ?? '').trim();
  return text && text !== '[object Object]' ? text : fallback;
};

const toSafeToken = (value: string): string =>
  value
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 120);

const readFileExtension = (fileName: string): string => {
  const index = fileName.lastIndexOf('.');
  if (index <= 0 || index >= fileName.length - 1) {
    return '';
  }
  return fileName.slice(index).toLowerCase();
};

const buildAdmissionsStoragePath = (
  applicationId: string,
  category: string,
  ownerId: string,
  documentName: string,
  sourceFileName: string,
): string => {
  const now = Date.now();
  const extension = readFileExtension(sourceFileName);
  const safeBaseName = toSafeToken(documentName) || toSafeToken(sourceFileName.replace(/\.[^.]+$/, '')) || 'document';
  const safeCategory = toSafeToken(category) || 'other';
  const safeOwner = toSafeToken(ownerId) || 'unknown';
  return `uploads/admissions/${applicationId}/${safeOwner}/${safeCategory}/${now}-${safeBaseName}${extension}`;
};

const isStoragePath = (value: string): boolean => /^(uploads|admissions)\//i.test(value);

const DOCUMENT_CATEGORY_LABELS: Record<string, string> = {
  tuition_contract: 'Tuition Contract',
  tuition_contract_signed: 'Signed Tuition Contract',
  signed_tuition_contract: 'Signed Tuition Contract',
  acceptance_letter: 'Acceptance Letter',
  application_document: 'Application Document',
  report_card: 'Report Card',
  birth_certificate: 'Birth Certificate',
  immunization_record: 'Immunization Record',
  recent_photo: 'Recent Photo',
  other: 'Other',
};

const humanizeDocumentToken = (value: string): string => {
  const normalized = value
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return 'Document';
  return normalized
    .split(' ')
    .map((word) => word.length <= 2 ? word.toUpperCase() : `${word[0]?.toUpperCase() ?? ''}${word.slice(1)}`)
    .join(' ');
};

const formatDocumentCategory = (category: string): string => {
  const normalized = readString(category).toLowerCase();
  return DOCUMENT_CATEGORY_LABELS[normalized] ?? humanizeDocumentToken(normalized || 'document');
};

const isOpaqueResourceId = (value: string): boolean =>
  /^res-[a-z0-9-]+$/i.test(value) || /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(value);

const resolveDocumentDisplayName = (resourceId: string, category = ''): string => {
  const categoryLabel = formatDocumentCategory(category || 'document');
  if (!resourceId || isOpaqueResourceId(resourceId)) return categoryLabel;
  if (!isStoragePath(resourceId)) {
    return humanizeDocumentToken(resourceId);
  }
  const lastSegment = resourceId.split('/').pop() || resourceId;
  const withoutPrefix = lastSegment.replace(/^\d{10,}-/, '');
  return humanizeDocumentToken(withoutPrefix) || categoryLabel;
};

const AddDocumentModal = ({
  isOpen,
  studentOptions,
  isSubmitting,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  studentOptions: StudentOption[];
  isSubmitting: boolean;
  onClose: () => void;
  onAdd: (docName: string, students: StudentOption[], file: File) => Promise<void>;
}) => {
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [documentName, setDocumentName] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSelectedStudents(studentOptions.length ? studentOptions.map((option) => option.key) : []);
      setDocumentName('');
      setDocumentFile(null);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, studentOptions]);

  if (!isOpen) return null;

  const toggleStudent = (studentKey: string) => {
    setSelectedStudents((prev) =>
      prev.includes(studentKey) ? prev.filter((entry) => entry !== studentKey) : [...prev, studentKey]
    );
  };

  const disabled = (
    isSubmitting
    || documentName.trim().length === 0
    || selectedStudents.length === 0
    || !documentFile
  );

  return (
    <AppDialogFrame
      open={isOpen}
      onClose={onClose}
      title="Add Document"
      description="Upload and assign the document to one or more students."
      testId="admissions-add-document-modal"
      maxWidthClassName="max-w-xl"
      titleClassName="text-xl"
      descriptionClassName="text-xs font-medium text-irongrey/40"
      bodyClassName="space-y-6"
      bodyPaddingClassName="p-8"
      footerPaddingClassName="px-8 py-6"
      footer={(
        <>
          <button onClick={onClose} className="px-6 py-2 text-sm font-bold text-irongrey/60 transition-colors hover:text-irongrey">
            Cancel
          </button>
          <button
            type="button"
            data-testid="admissions-add-document-submit"
            disabled={disabled}
            onClick={() => {
              if (!documentFile) return;
              const selectedOptions = studentOptions.filter((option) => selectedStudents.includes(option.key));
              void onAdd(documentName.trim(), selectedOptions, documentFile);
            }}
            className="rounded-xl bg-primary px-8 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50 disabled:shadow-none hover:bg-primary-hover"
          >
            {isSubmitting ? 'Adding…' : 'Add Document'}
          </button>
        </>
      )}
    >
          <div>
            <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-3 block">Apply to Students</label>
            <div className="flex gap-3">
              {studentOptions.map((student) => (
                <button
                  key={student.key}
                  type="button"
                  data-testid="admissions-add-document-student-chip"
                  data-child-application-id={student.childApplicationId ?? ''}
                  onClick={() => toggleStudent(student.key)}
                  className={`px-6 py-2 rounded-xl text-sm font-bold border transition-all ${
                    selectedStudents.includes(student.key)
                      ? 'bg-primary/10 border-primary text-primary shadow-sm'
                      : 'bg-white border-gray-100 text-irongrey/40 hover:border-gray-300'
                  }`}
                >
                  {student.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">Document Name (Required)</label>
            <input
              type="text"
              value={documentName}
              onChange={(event) => setDocumentName(event.target.value)}
              placeholder="e.g. Q1 Report Card"
              className="w-full bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary placeholder:text-irongrey/20 py-2.5 px-3"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">Document File (Required)</label>
            <label className="w-full flex items-center justify-between gap-4 bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-semibold text-irongrey py-2.5 px-3 cursor-pointer hover:border-primary/40 transition-colors">
              <input
                data-testid="admissions-add-document-file-input"
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(event) => setDocumentFile(event.currentTarget.files?.[0] ?? null)}
              />
              <span className="truncate">{documentFile ? documentFile.name : 'Choose a file'}</span>
              <span className="text-xs font-bold text-primary">Browse</span>
            </label>
          </div>
    </AppDialogFrame>
  );
};

export const DocumentsTab: React.FC = () => {
  const { applicationId } = useOutletContext<{ applicationId: string }>();
  const query = useAdmissionsDocuments(applicationId);
  const applicationQuery = useGetAdmissionsApplicationQuery(applicationId, { skip: !applicationId });
  const [addDocument, addDocumentState] = useAddAdmissionsDocumentMutation();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [mutationSuccess, setMutationSuccess] = useState<string | null>(null);
  const [viewingDocumentId, setViewingDocumentId] = useState<string | null>(null);

  const studentOptions = useMemo(() => {
    const application = applicationQuery.data as unknown as Record<string, unknown> | undefined;
    const children = Array.isArray(application?.children) ? application?.children : [];
    const options = children
      .map((entry, index) => {
        const record = asRecord(entry);
        const childInfo = asRecord(record.childInfo);
        const childApplicationId = readString(record.id);
        const label = `${readString(childInfo.firstName)} ${readString(childInfo.lastName)}`.trim();
        const fallbackLabel = label || `Student ${index + 1}`;
        return {
          key: childApplicationId || `child-${index}`,
          label: fallbackLabel,
          childApplicationId: childApplicationId || undefined,
        } as StudentOption;
      })
      .filter((option) => option.label.length > 0);

    const deduped = new Map<string, StudentOption>();
    for (const option of options) {
      if (!deduped.has(option.key)) {
        deduped.set(option.key, option);
      }
    }

    if (deduped.size > 0) return Array.from(deduped.values());
    return [{ key: 'all-students', label: 'All Students' }];
  }, [applicationQuery.data]);
  const defaultStudentLabel = studentOptions.map((option) => option.label).join(', ');
  const studentLabelByChildId = useMemo(() => {
    const mapping = new Map<string, string>();
    studentOptions.forEach((option) => {
      if (option.childApplicationId) {
        mapping.set(option.childApplicationId, option.label);
      }
    });
    return mapping;
  }, [studentOptions]);
  const resolveDocumentStudentLabel = (record: Record<string, unknown>): string => {
    const childIds = readStringArray(record.childApplicationIds);
    if (childIds.length === 0) {
      return defaultStudentLabel;
    }
    const labels = Array.from(new Set(childIds
      .map((childId) => studentLabelByChildId.get(childId) || '')
      .filter(Boolean)));
    if (labels.length > 0) {
      return labels.join(', ');
    }
    return `Associated to ${childIds.length} student${childIds.length === 1 ? '' : 's'}`;
  };

  const baseItems = query.data?.items ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return baseItems;
    return baseItems.filter((doc) => {
      const record = asRecord(doc);
      const haystack = [
        formatDocumentCategory(readString(record.category) || 'other'),
        resolveDocumentDisplayName(readString(record.resourceId), readString(record.category)),
        readString(record.fileName),
        resolveDocumentStudentLabel(record),
        readString(record.uploadedBy),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [baseItems, defaultStudentLabel, search, studentLabelByChildId]);

  const onAddDocument = async (docName: string, students: StudentOption[], file: File) => {
    if (!applicationId || !docName || !file) return;
    setMutationError(null);
    setMutationSuccess(null);
    const cognitoProfile = resolveCognitoProfile();
    const ownerId = readString(cognitoProfile?.userSub) || 'unknown';
    const category = 'other';
    const selectedStudentLabels = students.map((student) => student.label).filter(Boolean);
    const selectedChildApplicationIds = Array.from(
      new Set(
        students
          .map((student) => readString(student.childApplicationId))
          .filter(Boolean)
      )
    );
    const storagePath = buildAdmissionsStoragePath(applicationId, category, ownerId, docName, file.name);
    let uploaded = false;
    try {
      await uploadData({
        path: storagePath,
        data: file,
        options: {
          contentType: file.type || 'application/octet-stream',
          metadata: {
            applicationId,
            uploadedBy: ownerId,
            documentName: toSafeToken(docName) || 'document',
            students: selectedStudentLabels
              .map((name) => toSafeToken(name))
              .filter(Boolean)
              .slice(0, 8)
              .join(','),
          },
        },
      }).result;
      uploaded = true;

      await addDocument({
        applicationId,
        document: {
          resourceId: storagePath,
          category,
          uploadedBy: ownerId,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          childApplicationIds: selectedChildApplicationIds.length > 0 ? selectedChildApplicationIds : undefined,
          storageProvider: 's3',
          storageKey: storagePath,
        },
      }).unwrap();
      setMutationSuccess(`Document "${docName}" added.`);
      setIsModalOpen(false);
      await query.refetch();
    } catch (error) {
      if (uploaded) {
        await remove({ path: storagePath }).catch(() => undefined);
      }
      setMutationError(toErrorMessage(error, 'Unable to add document right now.'));
    }
  };

  const onViewDocument = async (documentId: string, resourceId: string) => {
    const value = readString(resourceId);
    if (!value) {
      setMutationError('Document preview is unavailable for this entry.');
      return;
    }
    setMutationError(null);
    setViewingDocumentId(documentId);
    try {
      if (/^https?:\/\//i.test(value)) {
        window.open(value, '_blank', 'noopener,noreferrer');
        return;
      }
      if (!isStoragePath(value)) {
        throw new Error('Document preview URL is not available for this entry.');
      }
      const signed = await getUrl({ path: value });
      window.open(signed.url.toString(), '_blank', 'noopener,noreferrer');
    } catch (error) {
      setMutationError(toErrorMessage(error, 'Unable to open this document right now.'));
    } finally {
      setViewingDocumentId(null);
    }
  };

  return (
    <section data-testid="admissions-detail-documents-tab" className="pb-10">
      <AddDocumentModal
        isOpen={isModalOpen}
        studentOptions={studentOptions}
        isSubmitting={addDocumentState.isLoading}
        onClose={() => setIsModalOpen(false)}
        onAdd={onAddDocument}
      />

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <FileText size={18} />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-irongrey">Documents</h2>
          <p className="text-sm text-irongrey/50">Uploaded documents for this application.</p>
        </div>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary-hover transition-all flex items-center gap-2 shrink-0"
        >
          <Plus size={18} />
          Add Document
        </button>
      </div>

      {mutationError ? <p className="mb-4 text-sm font-semibold text-irongrey">{mutationError}</p> : null}
      {mutationSuccess ? <p className="mb-4 text-sm font-semibold text-primary">{mutationSuccess}</p> : null}

      <div className="mb-4 flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2 shadow-inner">
        <Search size={16} className="text-irongrey/30" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search documents…"
          className="outline-none text-sm text-irongrey placeholder:text-irongrey/30 w-full"
        />
      </div>

      {query.isLoading ? <p className="text-sm text-irongrey/50">Loading…</p> : null}
      {query.error ? (
        <SupportErrorMessage>Unable to load documents right now.</SupportErrorMessage>
      ) : null}

      <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
        <div className="hidden md:grid md:grid-cols-12 gap-2 px-6 py-4 bg-gray-50/30 border-b border-gray-100">
          <div className="col-span-3 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Student</div>
          <div className="col-span-3 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Document Name</div>
          <div className="col-span-2 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Category</div>
          <div className="col-span-2 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Status</div>
          <div className="col-span-2 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest text-right">Actions</div>
        </div>

        {filtered.length === 0 && !query.isLoading ? (
          <div className="px-6 py-10 text-center">
            <p className="text-sm text-irongrey/50 italic">Document records are syncing.</p>
          </div>
        ) : null}

        <div className="divide-y divide-gray-50">
          {filtered.map((doc) => {
            const record = asRecord(doc);
            const id = readString(record.id);
            const rawResourceId = readString(record.resourceId);
            const rawCategory = readString(record.category) || 'other';
            const documentName = readString(record.fileName) || resolveDocumentDisplayName(rawResourceId, rawCategory);
            const category = formatDocumentCategory(rawCategory);
            const uploadedDate = formatDate(record.uploadedAt);
            const studentLabel = resolveDocumentStudentLabel(record);
            const associatedChildIds = readStringArray(record.childApplicationIds);
            const viewing = viewingDocumentId === id;
            return (
              <div
                key={id}
                data-testid="admissions-document-row"
                data-associated-child-ids={associatedChildIds.join(',')}
                className="px-6 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start hover:bg-gray-50/40 transition-colors"
              >
                <div className="md:col-span-3">
                  <p className="text-sm font-bold text-irongrey">{studentLabel}</p>
                  <p className="text-[11px] text-irongrey/40 font-medium mt-1">
                    Uploaded {uploadedDate || 'recently'}
                  </p>
                </div>
                <div className="md:col-span-3">
                  <p className="text-sm font-semibold text-irongrey/80 break-all">{documentName}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-irongrey/70">{category}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                    Uploaded
                  </span>
                </div>
                <div className="md:col-span-2 flex md:justify-end">
                  <button
                    type="button"
                    onClick={() => void onViewDocument(id, rawResourceId)}
                    disabled={viewing}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-irongrey/70 hover:border-gray-300 hover:text-irongrey transition-colors"
                  >
                    {viewing ? 'Opening…' : 'View'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
